import { normalizeMfaDestinationPhoneE164 } from './mfa-challenge-contract';

/**
 * SMS marketing contact capture. Verifying a number here never creates an MFA
 * factor, and MFA enrollment never grants this consent.
 */

/** Shows the signup on the main page, onboarding and settings; off wherever the flag has not been created. */
export const SMS_MARKETING_FEATURE_FLAG = 'sms_marketing';

/** The main page SMS signup modal opens on a link to this anchor, even after it was closed. */
export const SMS_SIGNUP_MODAL_HASH = '#text-message-signup';
/** localStorage key set once the visitor closes the main page modal, so it stops opening by itself. */
export const SMS_SIGNUP_MODAL_DISMISSED_KEY = 'starthub.smsSignupModal.dismissed';

/** "Last updated" dates of the published Terms of Use and Privacy Policy. */
export const SMS_MARKETING_LEGAL_VERSIONS = {
  terms: '2026-09-27',
  privacy: '2026-10-09',
} as const;

/** Change whenever the consent text below changes, and keep the old wording in the history. */
const SMS_MARKETING_COPY_VERSION = '2026-10-29.1';

/** The wording registered with the carriers for StartHub's texting campaign; the box must show it word for word. */
const SMS_MARKETING_CONSENT_LABEL =
  'Yes, send me text messages from StartHub Academy about the StartHub MBA, including marketing messages and enrolment offers. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe, HELP for help. Consent is not a condition of purchase.';

/**
 * The consent copy a person sees. The version also names the linked Terms and
 * Privacy Policy, so stored evidence identifies the exact wording and documents,
 * and a page that showed older ones is refused.
 */
export const SMS_MARKETING_DISCLOSURE = {
  version: `${SMS_MARKETING_COPY_VERSION}+terms.${SMS_MARKETING_LEGAL_VERSIONS.terms}+privacy.${SMS_MARKETING_LEGAL_VERSIONS.privacy}`,
  consentLabel: SMS_MARKETING_CONSENT_LABEL,
  notAConditionNotice:
    'Agreeing to texts is not a condition of enrolling on any StartHub programme or of buying anything.',
  privacyNotice: 'We never sell, rent or share your mobile number or your consent with anyone for their own marketing.',
  policyLinks: { prefix: 'See our', privacyPolicy: 'Privacy Policy', conjunction: 'and', termsOfUse: 'Terms of Use' },
} as const;

const SMS_MARKETING_CONSENT_LABEL_2026_10_02 =
  'Text me about the StartHub MBA. Message and data rates may apply. Message frequency varies. Reply STOP to unsubscribe, HELP for help.';

/** The checkbox wording behind every disclosure version ever recorded, so evidence can name what each person saw. */
const SMS_MARKETING_CONSENT_LABELS: Readonly<Record<string, string>> = {
  '2026-10-02.2+terms.2026-09-27+privacy.2026-09-27': SMS_MARKETING_CONSENT_LABEL_2026_10_02,
  '2026-10-02.2+terms.2026-09-27+privacy.2026-10-09': SMS_MARKETING_CONSENT_LABEL_2026_10_02,
  [SMS_MARKETING_DISCLOSURE.version]: SMS_MARKETING_DISCLOSURE.consentLabel,
};

/** The exact checkbox wording a recorded disclosure version showed, or undefined for an unknown version. */
export function smsMarketingConsentLabelForVersion(version: string): string | undefined {
  return SMS_MARKETING_CONSENT_LABELS[version];
}

// NXX-NXX-XXXX with no N11 service code in either position.
const NANP_NATIONAL_NUMBER = /^(?![2-9]11)[2-9]\d{2}(?![2-9]11)[2-9]\d{6}$/;
const PHONE_INPUT_CHARACTERS = /^\+?[\d\s().-]+$/;

/**
 * Area codes that share +1 but are outside the United States (Canada and the
 * Caribbean), from the NANPA area code list. US territories stay allowed.
 */
const NON_US_NANP_AREA_CODES: ReadonlySet<string> = new Set([
  // Canada
  '204',
  '226',
  '236',
  '249',
  '250',
  '257',
  '263',
  '289',
  '306',
  '343',
  '354',
  '365',
  '367',
  '368',
  '382',
  '387',
  '403',
  '416',
  '418',
  '428',
  '431',
  '437',
  '438',
  '450',
  '460',
  '468',
  '474',
  '506',
  '514',
  '519',
  '548',
  '579',
  '581',
  '584',
  '587',
  '600',
  '604',
  '613',
  '622',
  '639',
  '647',
  '672',
  '683',
  '705',
  '709',
  '742',
  '753',
  '778',
  '780',
  '782',
  '807',
  '819',
  '825',
  '867',
  '873',
  '879',
  '902',
  '905',
  '942',
  // Caribbean and Bermuda
  '242',
  '246',
  '264',
  '268',
  '284',
  '345',
  '441',
  '473',
  '649',
  '658',
  '664',
  '721',
  '758',
  '767',
  '784',
  '809',
  '829',
  '849',
  '868',
  '869',
  '876',
]);

/** Parses a US number typed in a common format, such as `(555) 555-0100`, into E.164. */
export function normalizeUsPhoneToE164(input: string): string | undefined {
  const trimmed = input.trim();
  if (!PHONE_INPUT_CHARACTERS.test(trimmed)) {
    return undefined;
  }
  const digits = trimmed.replace(/\D/g, '');
  const hasCountryCode = digits.length === 11 && digits.startsWith('1');
  if (trimmed.startsWith('+') && !hasCountryCode) {
    return undefined;
  }
  const national = hasCountryCode ? digits.slice(1) : digits;
  if (!NANP_NATIONAL_NUMBER.test(national) || NON_US_NANP_AREA_CODES.has(national.slice(0, 3))) {
    return undefined;
  }
  // The shared E.164 rule stays the single canonical check for every stored phone.
  return normalizeMfaDestinationPhoneE164(`+1${national}`);
}
